import { ActionTree, ActionContext } from 'vuex';
import { map } from 'lodash';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { ServiceViewMutationTypes } from './mutation-types';
import { ServiceViewActionTypes } from './action-types';
import { ServiceApiOptions, PortfolioApiResponseResults } from './types';
import { KeyValuePair } from '@/types/Global';

import { ClientApiResponse } from '../bookView/types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [ServiceViewActionTypes.PORTFOLIO_DATA](
    { commit }: AugmentedAction,
    payload: ServiceApiOptions
  ): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [ServiceViewActionTypes.PORTFOLIO_DATA](
    { commit },
    filterOptions: ServiceApiOptions
  ) {
    try {
      const pff = filterOptions.portfolioFilterField;
      const pfid = filterOptions.portfolioFilterId;
      const serviceLevel = filterOptions.serviceLevel;
      const pageFilter = {
        key: pff,
        value: pfid,
      } as KeyValuePair;
      const serviceLevelFilter = {
        key: 'service_level_wide',
        value: serviceLevel.toUpperCase(),
      } as KeyValuePair;
      const filters = serviceLevel
        ? [pageFilter, serviceLevelFilter]
        : [pageFilter];

      const paramString = combineQueryParams({
        size: filterOptions.pageSize,
        start: filterOptions.pageStart,
        ['sort_descending']: filterOptions.sortDesc,
        ['sort_by']: filterOptions.sortKey,
        ['filters']: filters,
        ['search_field']: filterOptions.searchField,
        ['search_string']: filterOptions.searchString,
        ['aggregate_by']: 'portfolio',
      });

      const portfolioData: ClientApiResponse = await ServerAPI.get(
        `/clients${paramString}`
      );
      commit(ServiceViewMutationTypes.SET_PORTFOLIO_DATA, {
        portfolioData: map(
          portfolioData.results,
          item =>
            ({
              familyStatus: item.familyStatus,
              status: item.status,
              clientId: item.clientId,
              familyId: item.familyId,
              portfolioId: item.portfolioId,
              aum: item.aum,
              roa: item.roa,
              revenue: item.revenue,
              periodicFees: item.periodicFees,
              transactionFees: item.transactionFees,
              totalFees: item.totalFees,
              priceEnforcement: item.priceEnforcement,
              profit: item.profit,
              profitMargin: item.profitMargin,
              isOpening: item.isOpening,
              isClosing: item.isClosing,
              clientSpecificValues: item.clientSpecificValues,
            } as PortfolioApiResponseResults)
        ),
        portfolioDataCount: portfolioData.count,
      });
    } catch (e) {
      handleError(e);
      commit(ServiceViewMutationTypes.SET_PORTFOLIO_DATA, {
        portfolioData: [],
        portfolioDataCount: 0,
      });
    }
  },
};
